import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import ms from 'ms';
import { useEffect } from 'react';

import { toast } from '@hivebrite/design-system/Toast';

import { getMentoringProfileFromUser } from 'hb-react/frontoffice/apps/Mentoring/requests';
import type {
  MentorProfile,
  Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getProgramTopicId } from 'hb-react/frontoffice/apps/Mentoring/utils';
import { QUERY_KEYS } from 'hb-react/shared/apps/Mentoring/constants';

interface GetMentorProfileParams {
  program: Pick<Program, 'id' | 'parent_id' | 'parent_type'>;
  userId: number;
}

export const useGetMentorProfileFromUser = ({
  program,
  userId,
}: GetMentorProfileParams) => {
  const {
    id: programId,
    parent_id: parentId,
    parent_type: parentType,
  } = program;

  const { data, error, isLoading, isError } = useQuery<
    MentorProfile | { status: null } | undefined,
    AxiosError
  >({
    queryKey: [QUERY_KEYS.mentorProfileFromUser, programId, userId],
    queryFn: ({ signal }) =>
      getMentoringProfileFromUser({
        routeApi: Routes.mentorings_program_user_mentor_profile_path(
          programId,
          userId,
        ),
        signal,
        topicId: getProgramTopicId({ parentId, parentType }),
      }),
    staleTime: ms('5m'),
    enabled: !!userId,
  });

  useEffect(() => {
    if (error?.response?.status === 500) {
      toast.error(__('Sorry, an error occurred'));
    }
  }, [error]);

  return { data, error, isLoading, isError };
};
